import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import EN from "./constants/en";
import { injectCriticalCss } from "./utils/criticalCss";
import { initializeFirebase } from "./lib/firebase";

// Inject critical CSS as early as possible
injectCriticalCss();

// Handle Google Fonts loading without inline events
const handleGoogleFontsLoading = () => {
    const googleFontsLink = document.getElementById("google-fonts-link") as HTMLLinkElement;
    if (googleFontsLink) {
        googleFontsLink.addEventListener("load", () => {
            googleFontsLink.media = "all";
        });
    }
};

// Initialize font loading handler after DOM is ready
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", handleGoogleFontsLoading);
} else {
    handleGoogleFontsLoading();
}

// Register Service Worker for PWA functionality
if ("serviceWorker" in navigator && import.meta.env.PROD) {
    window.addEventListener("load", async () => {
        try {
            const registration = await navigator.serviceWorker.register("/sw.js", {
                scope: "/",
            });

            console.log("[PWA] Service Worker registered successfully:", registration.scope);

            // Handle service worker updates
            registration.addEventListener("updatefound", () => {
                const newWorker = registration.installing;
                if (newWorker) {
                    newWorker.addEventListener("statechange", () => {
                        if (newWorker.state === "installed") {
                            if (navigator.serviceWorker.controller) {
                                // New content available, show update notification
                                console.log("[PWA] New content available, please refresh");
                                // Optional: Show user notification about update
                                if (window.confirm("New content available! Click OK to refresh.")) {
                                    window.location.reload();
                                }
                            } else {
                                // Content cached for first time
                                console.log("[PWA] Content cached for offline use");
                            }
                        }
                    });
                }
            });
        } catch (error) {
            console.error("[PWA] Service Worker registration failed:", error);
        }
    });

    // Handle service worker controller changes
    navigator.serviceWorker.addEventListener("controllerchange", () => {
        console.log("[PWA] Service Worker controller changed, reloading page");
        window.location.reload();
    });
}

document.title = EN.META_TITLE;
document.querySelector('meta[name="description"]')?.setAttribute("content", EN.META_DESCRIPTION);
document.querySelector('meta[name="author"]')?.setAttribute("content", EN.META_AUTHOR);
document.querySelector('meta[property="og:title"]')?.setAttribute("content", EN.META_TITLE);
document.querySelector('meta[property="og:description"]')?.setAttribute("content", EN.META_DESCRIPTION);
document.querySelector('meta[property="og:type"]')?.setAttribute("content", EN.META_OG_TYPE);
document.querySelector('meta[property="og:image"]')?.setAttribute("content", EN.META_IMAGE);
document.querySelector('meta[name="twitter:site"]')?.setAttribute("content", EN.META_TWITTER_HANDLE);
document.querySelector('meta[name="twitter:image"]')?.setAttribute("content", EN.META_IMAGE);
document.querySelector('meta[name="twitter:card"]')?.setAttribute("content", EN.META_TWITTER_CARD);

createRoot(document.getElementById("root")!).render(<App />);

// Initialize Firebase after app loads
window.addEventListener("load", async () => {
    try {
        await initializeFirebase();
    } catch (error) {
        console.error("Firebase initialization failed:", error);
    }
});
